import { AppointmentConfirmationStatus } from '@app/graphql/types/graphql.ts';
import Card from '@app/components/card/card.tsx';
import { ClockIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useTranslate } from '@tolgee/react';
import { CheckCircleIcon } from '@heroicons/react/16/solid';
import CardContent from '@app/components/card/card-content.tsx';
import { AppointmentStatusNotificationType } from '@app/page/appointment/appointment-track/component/appointment-status-notification-type.ts';
import { useEffect, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import PrimaryButton from '@app/components/primary-button.tsx';
import { APPOINTMENT_DONE_STATE } from '@app/page/appointment/appointment-create/appointment-create.tsx';

export default function AppointmentStatusNotification(props: {
  state?: AppointmentConfirmationStatus | null;
  phone: string;
  time: string;
  mode: AppointmentStatusNotificationType;
}) {
  const { t } = useTranslate();
  const [isFirstAccess, setIsFirstAccess] = useState(false);

  useEffect(() => {
    const storedTime = localStorage.getItem(APPOINTMENT_DONE_STATE);

    if (!storedTime) {
      return;
    }

    const elapsedTime = Date.now() - parseInt(storedTime, 10);

    if (elapsedTime < 15000) {
      setIsFirstAccess(true);
    }
  }, [setIsFirstAccess]);

  if (!props.state) {
    return null;
  }

  const content = (
    <div
      onClick={() => {
        window.location.reload();
      }}
      className="flex flex-col items-center"
    >
      {/*Header*/}
      <div className="flex flex-col items-center gap-4">
        {props.state == AppointmentConfirmationStatus.Waiting && (
          <>
            <ClockIcon className="h-16 w-16 text-yellow-500" />
            <p className="text-center text-3xl font-medium text-yellow-500">
              {t('appointment.status-noti.pending.header', 'Your appointment is awaiting confirmation')}
            </p>
          </>
        )}

        {props.state == AppointmentConfirmationStatus.Confirmed && (
          <>
            <CheckCircleIcon className="h-16 w-16 text-green-500" />
            <p className="text-center text-3xl font-medium text-green-500">
              {t('appointment.status-noti.confirm.header', 'Your appointment is accepted.')}
            </p>
            <p className="my-2 text-3xl">{props.time}</p>
          </>
        )}

        {props.state == AppointmentConfirmationStatus.Cancelled && (
          <>
            <XCircleIcon className="h-16 w-16 text-center text-red-500" />
            <p className="text-3xl font-medium text-red-500">
              {t('appointment.status-noti.cancelled.header', 'Your appointment has been cancelled')}
            </p>
          </>
        )}
      </div>

      {/*Content*/}
      <div className="mt-4 text-lg text-gray-500">
        {props.phone && (
          <p>
            1.{' '}
            {t('appointment.status-noti.phone', 'If you have any questions, please contact the restaurant by phone at')}{' '}
            <a
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="underline"
              href={`tel:${props.phone}`}
            >
              {props.phone}
            </a>
          </p>
        )}
        <p>
          2. <span>{t('appointment.status-noti.update-on-1', 'You will receive confirmation and updates')}</span>{' '}
          <span className="underline">{t('appointment.status-noti.update-on-2', 'on this page or via email. ')}</span>
        </p>
        <p>
          3. <span>{t('appointment.status-noti.check-mail-1', 'Please check email')}</span>{' '}
          <span className="underline">{t('appointment.status-noti.check-mail-2', 'in your spam folder')}</span>{' '}
          <span>
            {t(
              'appointment.status-noti.check-mail-3',
              'and mark our email address as a priority to avoid missing any notifications.'
            )}
          </span>
        </p>
        <p>4. {t('appointment.status-noti.refresh', 'Press F5 or click here to update latest status.')}</p>
      </div>
    </div>
  );

  if (props.mode === AppointmentStatusNotificationType.CARD) {
    return (
      <Card>
        <CardContent>{content}</CardContent>
      </Card>
    );
  }

  return (
    <Dialog
      open={isFirstAccess}
      className="relative z-10 focus:outline-none"
      onClose={() => {
        setIsFirstAccess(false);
      }}
    >
      <DialogBackdrop transition={true} className="fixed inset-0 bg-black/30" />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel
            transition
            className="max-w-[1080px] rounded-xl border bg-white p-6 backdrop-blur-2xl duration-300 ease-out data-[closed]:opacity-0"
          >
            {content}
            <div className="mt-6 flex w-full justify-center">
              <PrimaryButton
                className="h-10 w-48 p-2 text-lg"
                onClick={() => {
                  setIsFirstAccess(false);
                }}
              >
                {t('common.button.continue', 'Continue')}
              </PrimaryButton>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
